import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Phone, Menu, ChevronDown, MapPin, Clock, Mail, Facebook, Instagram, Linkedin, ArrowRight, Compass, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from './ui/sheet';
import { COMPANY, NAV_SERVICES, NAV_ALL_SERVICES, SOCIAL_LINKS, COMPANY_FAMILY } from '../lib/siteConfig';
import { FOOTER_SERVICE_AREAS } from '../data/cityIndex'; // lightweight slug/name registry — keeps the 50 KB of city copy out of the shared bundle
import { trackCall, trackEvent } from '../lib/analytics';
import { GoogleRatingLine, useReviewSummary, REVIEWS_MENU_MIN } from '../lib/siteData';
import { YelpIcon } from './icons/YelpIcon';

/*
 * Mobile action bar (SEO Batch B, Part 4): on every page under 768px EXCEPT /review (the bare
 * customer review link), /contact, and the calculator steps — /estimate-calculator is the step
 * wizard from its first screen. App.js uses this for the bar itself; the footer uses it for the
 * matching bottom spacer so a page never reserves room for a bar it does not show.
 */
const STICKY_BAR_EXCLUDED = new Set(['/review', '/contact', '/estimate-calculator']);
export const stickyBarShownOn = (pathname) => !STICKY_BAR_EXCLUDED.has((pathname || '/').replace(/\/+$/, '') || '/');

export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Route changes jump to the top instantly. `html { scroll-behavior: smooth }` (kept for in-page
    // anchors) would otherwise animate the new page scrolling past for ~1s after a footer link.
    const root = document.documentElement;
    const prev = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    void root.getBoundingClientRect(); // force the style override to apply before the scroll (Chrome ignores it otherwise)
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    root.style.scrollBehavior = prev;
  }, [pathname]);
  return null;
};

const Logo = () => (
  <Link to="/" data-testid="site-logo" className="flex items-center gap-2.5 group max-md:min-h-11">
    <img src="/logo-icon-white.png" alt="IDB HVAC Solutions logo" width={96} height={92} className="h-9 w-9 object-contain" />
    <span className="leading-none">
      <span className="block font-display font-semibold text-[15px] text-[#F4F1EA] tracking-tight">IDB HVAC</span>
      {/* Mobile polish §3: 10px → 12px under 768px (tracking eased so the line stays the wordmark's width). */}
      <span className="block text-[10px] max-md:text-xs uppercase tracking-[0.22em] max-md:tracking-[0.14em] text-[#8FA0AE]">Solutions</span>
    </span>
  </Link>
);

export const Header = () => {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  // Reviews joins the main menu only once there are three or more on-site reviews (Batch B, Part 3.4).
  // The count arrives client-side after hydration, so the first render matches the snapshot.
  const { count: reviewCount } = useReviewSummary();
  const showReviews = reviewCount >= REVIEWS_MENU_MIN;
  useEffect(() => { setServicesOpen(false); setMobileOpen(false); }, [location.pathname, setServicesOpen, setMobileOpen]);

  const navLink = 'text-sm font-medium text-[#C9D2DA] hover:text-white transition-colors px-3 py-2 rounded-md';
  const active = ({ isActive }) => `${navLink} ${isActive ? 'text-white' : ''}`;

  return (
    <header className="sticky top-0 z-50 bg-[#121417]/95 backdrop-blur border-b border-[#2A313A]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {/* The trigger is itself a real link to /services (crawlable, works without JS); the
              dropdown opens on hover / keyboard focus and lists "All services" first. */}
          <div className="relative" onMouseLeave={() => setServicesOpen(false)}>
            <NavLink
              to="/services"
              data-testid="nav-services-dropdown"
              className={({ isActive }) => `${navLink} inline-flex items-center gap-1 ${isActive || servicesOpen ? 'text-white' : ''}`}
              aria-haspopup="true"
              aria-expanded={servicesOpen}
              onMouseEnter={() => setServicesOpen(true)}
              onFocus={() => setServicesOpen(true)}
            >
              Services <ChevronDown className={`h-3.5 w-3.5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </NavLink>
            {servicesOpen && (
              <div className="absolute left-0 top-full pt-2 w-[380px]">
                <div className="rounded-xl bg-[#1B2026] border border-[#2A313A] shadow-2xl p-2">
                  <Link to={NAV_ALL_SERVICES.to} data-testid="nav-service-all" className="block rounded-lg px-3 py-2.5 hover:bg-white/5 border-b border-[#2A313A] mb-1">
                    <span className="block text-sm font-medium text-white">All services</span>
                    <span className="block text-xs text-[#8FA0AE] mt-0.5">{NAV_ALL_SERVICES.desc}</span>
                  </Link>
                  {NAV_SERVICES.map((s) => (
                    <Link key={s.to} to={s.to} data-testid={`nav-service-${s.to.replace(/\//g, '')}`} className="block rounded-lg px-3 py-2.5 hover:bg-white/5">
                      <span className="block text-sm font-medium text-white">{s.label}</span>
                      <span className="block text-xs text-[#8FA0AE] mt-0.5">{s.desc}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/estimate-calculator" className={active} data-testid="nav-calculator">Calculator</NavLink>
          <NavLink to="/service-areas" className={active} data-testid="nav-service-areas">Service Areas</NavLink>
          <NavLink to="/about" className={active} data-testid="nav-about">About</NavLink>
          {showReviews && <NavLink to="/reviews" className={active} data-testid="nav-reviews">Reviews</NavLink>}
          <NavLink to="/financing" className={active} data-testid="nav-financing">Financing</NavLink>
          <NavLink to="/contact" className={active} data-testid="nav-contact">Contact</NavLink>
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={COMPANY.phoneMainHref}
            onClick={() => trackCall('header')}
            data-testid="site-header-call-button"
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#F4F1EA] hover:text-white"
          >
            <Phone className="h-4 w-4 text-[#7FA7B8]" /> {COMPANY.phoneMain}
          </a>
          <Button asChild className="hidden sm:inline-flex h-10 px-4 idb-btn-copper rounded-[10px] active:scale-[0.98]" data-testid="header-free-estimate-button">
            <Link to="/estimate-calculator">Free Pre-Estimate</Link>
          </Button>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2.5 -mr-1 text-[#F4F1EA] inline-flex items-center justify-center" aria-label="Open menu" data-testid="mobile-menu-button">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#121417] border-[#2A313A] text-white w-[300px] overflow-y-auto">
              <SheetTitle className="text-white font-display">Menu</SheetTitle>
              <nav className="mt-6 flex flex-col gap-1" aria-label="Mobile">
                <p className="px-3 pt-2 pb-1 text-xs uppercase tracking-[0.2em] text-[#8FA0AE]">Services</p>
                <Link to={NAV_ALL_SERVICES.to} className="px-3 py-2.5 min-h-11 flex items-center rounded-lg text-sm font-medium text-[#C9D2DA] hover:bg-white/5 hover:text-white" data-testid="mobile-nav-service-all">All services</Link>
                {NAV_SERVICES.map((s) => (
                  <Link key={s.to} to={s.to} className="px-3 py-2.5 min-h-11 flex items-center rounded-lg text-sm font-medium text-[#C9D2DA] hover:bg-white/5 hover:text-white">{s.label}</Link>
                ))}
                <div className="my-2 h-px bg-[#2A313A]" />
                {[['Calculator', '/estimate-calculator'], ['Service Areas', '/service-areas'], ['About', '/about'], ...(showReviews ? [['Reviews', '/reviews']] : []), ['Financing', '/financing'], ['Projects', '/projects'], ['FAQ', '/faq'], ['Blog', '/blog'], ['Contact', '/contact']].map(([label, to]) => (
                  <Link key={to} to={to} className="px-3 py-2.5 min-h-11 flex items-center rounded-lg text-sm font-medium text-[#C9D2DA] hover:bg-white/5 hover:text-white" data-testid={`mobile-nav-${to.replace(/\//g, '')}`}>{label}</Link>
                ))}
                <a href={COMPANY.phoneMainHref} onClick={() => trackCall('mobile_menu')} className="mt-4 inline-flex items-center gap-2 px-3 py-2.5 min-h-11 rounded-lg bg-[#3E6F8E] text-white text-sm font-semibold">
                  <Phone className="h-4 w-4" /> Call {COMPANY.phoneMain}
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

/*
 * Paid-search landing page chrome (/residential-hvac-installation). A visitor who arrived from
 * an ad should see exactly two things to do: fill in the hero form or tap to call. So: logo
 * (home link kept for trust), the phone number, no primary navigation, no menu sheet, no
 * "Free Pre-Estimate" button competing with the form. Same height as the main header so the
 * hero sits at the identical offset and the pre-rendered snapshot does not shift on hydration.
 */
export const LandingHeader = () => (
  <header className="sticky top-0 z-50 bg-[#121417]/95 backdrop-blur border-b border-[#2A313A]" data-testid="lp-header">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
      <Logo />
      <a
        href={COMPANY.phoneMainHref}
        onClick={() => trackCall('header')}
        data-testid="lp-header-call-button"
        className="inline-flex items-center gap-2 h-10 max-md:h-11 px-3 sm:px-4 rounded-[10px] border border-[rgba(244,241,234,0.22)] text-sm font-semibold text-[#F4F1EA] hover:border-[rgba(244,241,234,0.4)] hover:bg-[rgba(255,252,246,0.06)] transition-colors duration-200 active:scale-[0.98]"
      >
        <Phone className="h-4 w-4 text-[#7FA7B8]" />
        <span className="hidden sm:inline">Call</span> {COMPANY.phoneMain}
      </a>
    </div>
  </header>
);

/* Slim landing-page footer: legal identity + the two legal links + one more way to call. Nothing else to wander off to. */
export const LandingFooter = () => (
  <footer className="bg-[#121417] text-[#8FA0AE] idb-grain" data-testid="lp-footer">
    <div className="idb-gradient-hairline" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs">
      <div>
        <p className="font-mono-idb text-[#E7C9A6]" data-testid="lp-footer-license-line">{COMPANY.licenseLine}</p>
        <p className="mt-1.5">© {new Date().getFullYear()} IDB HVAC Solutions · {COMPANY.addressFederalWay}, Federal Way, WA · {COMPANY.hours}</p>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <a href={COMPANY.phoneMainHref} onClick={() => trackCall('lp_footer')} className="inline-flex items-center gap-1.5 font-semibold text-[#F4F1EA] hover:text-white max-md:min-h-11" data-testid="lp-footer-call">
          <Phone className="h-3.5 w-3.5 text-[#7FA7B8]" /> {COMPANY.phoneMain}
        </a>
        <Link to="/privacy-policy" className="hover:text-white max-md:min-h-11 max-md:inline-flex max-md:items-center" data-testid="lp-footer-privacy">Privacy Policy</Link>
        <Link to="/terms-of-service" className="hover:text-white max-md:min-h-11 max-md:inline-flex max-md:items-center" data-testid="lp-footer-terms">Terms of Service</Link>
      </div>
    </div>
    <StickyBarSpacer />
  </footer>
);

/* Room for the fixed mobile bar (its ~56px height + the iPhone home-indicator safe area), phones only —
   and only on pages that actually show the bar, so /contact and the calculator keep a flush footer. */
const StickyBarSpacer = () => {
  const { pathname } = useLocation();
  if (!stickyBarShownOn(pathname)) return null;
  return <div className="md:hidden h-[calc(3.5rem+env(safe-area-inset-bottom))]" aria-hidden="true" />;
};

/*
 * Mobile action bar (SEO Batch B, Part 4). Two equal buttons, about 56px tall (6px + 44px + 6px),
 * padded past the iPhone home indicator (viewport-fit=cover is set in public/index.html).
 *   Call → tel:+14254055365, pushes the existing tap_to_call event with source "sticky_bar"
 *   Free Pre-Estimate → /estimate-calculator, pushes cta_estimate_click with location "sticky_bar"
 * App.js hides it on /review, /contact and the calculator steps (stickyBarShownOn above).
 */
export const MobileStickyBar = () => (
  <div className="fixed bottom-0 inset-x-0 z-50 md:hidden bg-[#FFFCF6]/95 backdrop-blur border-t border-[#D7D2C8] px-3 pt-1.5 pb-[max(6px,env(safe-area-inset-bottom))] flex gap-3" data-testid="mobile-sticky-bar">
    <a
      href={COMPANY.phoneMainHref}
      onClick={() => trackCall('sticky_bar')}
      data-testid="sticky-call-button"
      className="flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-[10px] border border-[#121417]/25 text-[#121417] font-semibold text-sm bg-transparent active:scale-[0.98]"
    >
      <Phone className="h-4 w-4" aria-hidden="true" /> Call
    </a>
    <Link
      to="/estimate-calculator"
      onClick={() => trackEvent('cta_estimate_click', { location: 'sticky_bar' })}
      data-testid="sticky-estimate-button"
      className="flex-1 h-11 inline-flex items-center justify-center rounded-[10px] idb-btn-copper text-sm active:scale-[0.98]"
    >
      Free Pre-Estimate
    </Link>
  </div>
);

/* Google Business Profile links (open in a new tab). Keep in step with SOCIAL_LINKS / the GBP listing. */
const FOOTER_GOOGLE_LINKS = [
  ['Leave us a Google review', SOCIAL_LINKS.googleReview, 'footer-google-review-link'],
  ['Find us on Google Maps', SOCIAL_LINKS.googleMaps, 'footer-google-maps-link'],
];

const FOOTER_QUICK_LINKS = [['Pre-Estimate Calculator', '/estimate-calculator'], ['Service Areas', '/service-areas'], ['Financing', '/financing'], ['Projects', '/projects'], ['Reviews', '/reviews'], ['FAQ', '/faq'], ['Blog', '/blog'], ['About', '/about'], ['Contact', '/contact']];

/* Footer social row: the four public profiles (Yelp added in SEO Batch B, Part 2.3). */
const FOOTER_SOCIAL = [[Facebook, 'Facebook', SOCIAL_LINKS.facebook], [Instagram, 'Instagram', SOCIAL_LINKS.instagram], [Linkedin, 'LinkedIn', SOCIAL_LINKS.linkedin], [YelpIcon, 'Yelp', SOCIAL_LINKS.yelp]];

/* Footer link row: 44px tap target under 768px (mobile polish §2); plain inline text from md up. */
const footerRow = 'max-md:min-h-11 max-md:flex max-md:items-center';

/*
 * Footer column that collapses to an accordion on phones (mobile polish §6): a 48px header button
 * toggles the list, closed by default; from md up the heading and list render exactly as before.
 * The links stay in the DOM either way (CSS-hidden), so crawlers and find-in-page still see them.
 */
const FooterGroup = ({ title, children, testId, className = '' }) => {
  const [open, setOpen] = useState(false);
  const id = `footer-group-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className={className} data-testid={testId}>
      <h3 className="text-sm font-semibold text-white uppercase tracking-[0.15em] max-md:hidden">{title}</h3>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={id}
        data-testid={`${id}-toggle`}
        className="md:hidden w-full min-h-12 flex items-center justify-between gap-3 text-left text-sm font-semibold text-white uppercase tracking-[0.15em] border-b border-[#2A313A]"
      >
        {title}
        <ChevronDown className={`h-4 w-4 text-[#7FA7B8] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      <div id={id} className={open ? '' : 'max-md:hidden'}>{children}</div>
    </div>
  );
};

export const Footer = () => (
  <footer className="bg-[#121417] text-[#C9D2DA] idb-grain" data-testid="site-footer">
    <div className="idb-gradient-hairline" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 max-md:py-10">
      {/* Five columns from xl (SEO Batch B added "Our companies"); three at lg, two at md, one on phones. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-10 max-md:gap-5 xl:gap-8">
        <div>
          <Link to="/" aria-label="IDB HVAC Solutions home" className="inline-flex max-md:min-h-11 items-center">
            <img src="/logo-white.png" alt="IDB HVAC Solutions — Quality HVAC Services" width={384} height={187} className="w-48 max-w-full object-contain" loading="lazy" />
          </Link>
          <p className="mt-4 text-sm leading-relaxed text-[#8FA0AE]">
            Residential, commercial, and industrial HVAC installation across the Puget Sound region. Sized right, installed clean, priced straight.
          </p>
          <p className="mt-4 font-mono-idb text-xs text-[#E7C9A6]" data-testid="footer-license-line">{COMPANY.licenseLine}</p>
          <p className="mt-2 inline-flex items-center gap-2 text-sm"><Clock className="h-4 w-4 text-[#7FA7B8]" /> {COMPANY.hours}</p>
          {/* "{rating}★ on Google ({count} reviews)" — only once the owner has entered a Google count of 1+ (Batch B, Part 3.5). */}
          <GoogleRatingLine className="mt-2 text-sm text-[#E7C9A6] hover:text-white" testId="footer-google-rating-line" />
          <div className="mt-4 flex gap-3" data-testid="footer-social-links">
            {FOOTER_SOCIAL.map(([Icon, label, href]) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={`IDB HVAC Solutions on ${label}`} data-testid={`footer-social-${label.toLowerCase()}`} className="p-2 max-md:h-11 max-md:w-11 max-md:p-0 max-md:inline-flex max-md:items-center max-md:justify-center rounded-lg border border-[#2A313A] hover:bg-white/5 transition-colors"><Icon className="h-4 w-4" /></a>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white uppercase tracking-[0.15em]">Our Offices</h3>
          <div className="mt-4 space-y-5 max-md:space-y-4 text-sm">
            <div>
              <p className="font-medium text-white inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-[#7FA7B8]" /> Federal Way HQ — South Sound</p>
              <p className="mt-1 text-[#8FA0AE]">{COMPANY.addressFederalWay}, Federal Way, WA</p>
              <a href={COMPANY.phoneMainHref} onClick={() => trackCall('footer_fw')} className={`mt-1 block text-[#E7C9A6] hover:text-white ${footerRow}`} data-testid="footer-phone-federal-way">{COMPANY.phoneMain}</a>
            </div>
            <div>
              <p className="font-medium text-white inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-[#7FA7B8]" /> Snohomish Branch — North Sound</p>
              <p className="mt-1 text-[#8FA0AE]">North Sound crews, dispatched by appointment</p>
              <a href={COMPANY.phoneMainHref} onClick={() => trackCall('footer_sno')} className={`mt-1 block text-[#E7C9A6] hover:text-white ${footerRow}`} data-testid="footer-phone-snohomish">{COMPANY.phoneMain}</a>
            </div>
            <a href={`mailto:${COMPANY.email}`} className={`inline-flex items-center gap-2 text-[#C9D2DA] hover:text-white ${footerRow}`} data-testid="footer-email"><Mail className="h-4 w-4 text-[#7FA7B8]" /> {COMPANY.email}</a>
          </div>
        </div>
        <FooterGroup title="Services">
          <ul className="mt-4 max-md:mt-2 space-y-2 max-md:space-y-0 text-sm" data-testid="footer-services">
            <li><Link to={NAV_ALL_SERVICES.to} className={`font-medium text-white hover:text-[#E7C9A6] ${footerRow}`} data-testid="footer-all-services-link">All Services</Link></li>
            {NAV_SERVICES.map((s) => (
              <li key={s.to}><Link to={s.to} className={`hover:text-white ${footerRow}`}>{s.label}</Link></li>
            ))}
          </ul>
        </FooterGroup>
        <FooterGroup title="Quick Links">
          <ul className="mt-4 max-md:mt-2 space-y-2 max-md:space-y-0 text-sm">
            {FOOTER_QUICK_LINKS.map(([label, to]) => (
              <li key={to}><Link to={to} className={`hover:text-white ${footerRow}`}>{label}</Link></li>
            ))}
            {FOOTER_GOOGLE_LINKS.map(([label, href, testId]) => (
              <li key={href}>
                <a href={href} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 hover:text-white ${footerRow}`} data-testid={testId}>
                  {label} <ExternalLink className="h-3.5 w-3.5 text-[#7FA7B8]" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </FooterGroup>
        {/* "Our companies" (SEO Batch B, Part 2.1): the ID Builder family — this site plus the two sister companies. */}
        <FooterGroup title="Our companies" testId="footer-our-companies">
          <ul className="mt-4 max-md:mt-2 space-y-3 max-md:space-y-1 text-sm">
            {COMPANY_FAMILY.map((c) => (
              <li key={c.key}>
                {c.internal ? (
                  <Link to={c.href} className={`font-medium text-white hover:text-[#E7C9A6] ${footerRow}`} data-testid={`footer-company-${c.key}`}>{c.name}</Link>
                ) : (
                  <a href={c.href} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 font-medium text-white hover:text-[#E7C9A6] ${footerRow}`} data-testid={`footer-company-${c.key}`}>
                    {c.name} <ExternalLink className="h-3.5 w-3.5 text-[#7FA7B8]" aria-hidden="true" />
                  </a>
                )}
                <p className="text-xs leading-relaxed text-[#8FA0AE] max-md:-mt-1 max-md:pb-2">{c.desc}</p>
              </li>
            ))}
          </ul>
        </FooterGroup>
      </div>
      {/* Service Areas: every one of the 18 /service-areas/* pages as a plain anchor (sitewide,
          crawlable support for each sitemap URL), grouped by the office that dispatches to it. */}
      <FooterGroup title="Service Areas" testId="footer-service-areas" className="mt-12 max-md:mt-5 pt-10 max-md:pt-0 border-t max-md:border-t-0 border-[#2A313A]">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 max-md:mt-3">
          <p className="mt-2 max-md:mt-0 text-sm leading-relaxed text-[#8FA0AE] max-w-2xl" data-testid="footer-service-area">
            <Compass className="inline h-4 w-4 text-[#7FA7B8] mr-1.5 -mt-0.5" />
            A 60-mile service radius from each office — Federal Way HQ covers the South Sound, our Snohomish branch covers the North Sound, and our crews meet in the middle.
          </p>
          <Link to="/service-areas" className={`inline-flex items-center gap-1.5 text-sm font-medium text-[#E7C9A6] hover:text-white shrink-0 ${footerRow}`} data-testid="footer-service-area-link">
            Every city we serve <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="mt-6 max-md:mt-3 grid sm:grid-cols-2 gap-8 max-md:gap-4">
          {FOOTER_SERVICE_AREAS.map((group) => (
            <div key={group.heading}>
              <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7FA7B8]">{group.heading}</h4>
              <ul className="mt-3 max-md:mt-1 flex flex-wrap gap-x-4 gap-y-2 max-md:gap-y-0 text-sm" aria-label={`${group.heading} service areas`} data-testid={`footer-areas-${group.heading.toLowerCase().replace(/\s+/g, '-')}`}>
                {group.links.map(([label, to]) => (
                  <li key={to}><Link to={to} className={`text-[#C9D2DA] hover:text-white underline-offset-4 hover:underline ${footerRow}`} data-testid={`footer-city-${to.split('/').pop()}`}>{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </FooterGroup>
      <div className="mt-12 max-md:mt-6 rounded-2xl bg-[#1B2026] border border-[#2A313A] p-6 max-md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 idb-brackets-dark relative">
        <div>
          <p className="font-display font-semibold text-white">Planning a new system?</p>
          <p className="text-sm text-[#8FA0AE]">Get an honest range in about two minutes — no sales visit required.</p>
        </div>
        <Link to="/estimate-calculator" data-testid="footer-cta-button" className="h-11 px-5 inline-flex items-center rounded-[10px] idb-btn-copper text-sm shrink-0 active:scale-[0.98]">
          Get My Estimate Range
        </Link>
      </div>
      <div className="mt-10 max-md:mt-6 pt-6 border-t border-[#2A313A] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[#8FA0AE]">
        <p>© {new Date().getFullYear()} IDB HVAC Solutions. All rights reserved.</p>
        <div className="flex gap-4">
          <Link to="/privacy-policy" className={`hover:text-white ${footerRow}`}>Privacy Policy</Link>
          <Link to="/terms-of-service" className={`hover:text-white ${footerRow}`}>Terms of Service</Link>
        </div>
      </div>
    </div>
    <StickyBarSpacer />
  </footer>
);
